/**
 * Seiten-Panel mit allen Urlauben EINES Mitarbeiters (Vergangenheit + Zukunft,
 * alle Status) zur schnellen Bearbeitung: Zeitraum ändern, Tage entfernen,
 * stornieren/löschen und neu eintragen.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { de } from 'date-fns/locale';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '../ui/sheet';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Plus, Pencil, CalendarX, Trash2, Loader2, Palmtree, History, ChevronDown,
} from 'lucide-react';
import { vacationsAPI } from '../../lib/api';
import { getStatusBadge } from '../../pages/vacations/VacationCalendarView';
import { VacationEditorDialog } from './VacationEditorDialog';
import { RemoveDaysDialog } from './RemoveDaysDialog';
import { toast } from 'sonner';

const ACTION_LABELS = {
  created: 'Angelegt',
  edited: 'Bearbeitet',
  days_removed: 'Tage entfernt',
  cancelled: 'Storniert',
  split: 'Aufgeteilt',
};

const fmtDateTime = (iso) => {
  try {
    return format(parseISO(iso), 'dd.MM.yyyy HH:mm', { locale: de });
  } catch {
    return iso;
  }
};

export function EmployeeVacationsSheet({ open, onOpenChange, employee, onChanged }) {
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorMode, setEditorMode] = useState('edit');
  const [editorVacation, setEditorVacation] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [removeVacation, setRemoveVacation] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  const toggleHistory = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const load = useCallback(async () => {
    if (!employee) return;
    setLoading(true);
    try {
      const res = await vacationsAPI.getAll({ employee_id: employee.employee_id });
      setVacations(res.data || []);
    } catch {
      toast.error('Fehler beim Laden der Urlaube');
    } finally {
      setLoading(false);
    }
  }, [employee]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const afterChange = () => { load(); onChanged && onChanged(); };

  const openEdit = (v) => { setEditorMode('edit'); setEditorVacation(v); setEditorOpen(true); };
  const openNew = () => { setEditorMode('new'); setEditorVacation(null); setEditorOpen(true); };

  const handleDelete = async (v) => {
    const isApproved = v.status === 'approved';
    if (!window.confirm(isApproved ? 'Diesen genehmigten Urlaub stornieren?' : 'Diesen Urlaubsantrag löschen?')) return;
    setBusyId(v.id);
    try {
      await vacationsAPI.delete(v.id);
      toast.success(isApproved ? 'Urlaub storniert' : 'Antrag gelöscht');
      afterChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler');
    } finally {
      setBusyId(null);
    }
  };

  const editable = (v) => v.status === 'pending' || v.status === 'approved';

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="employee-vacations-sheet">
          <SheetHeader>
            <SheetTitle className="font-heading flex items-center gap-2">
              <Palmtree className="h-5 w-5 text-primary" />
              {employee?.employee_name}
            </SheetTitle>
            <SheetDescription>
              {employee && (
                <>Verfügbar: <strong>{employee.available_days}</strong> · Genommen: {employee.used_days} · Beantragt: {employee.pending_days} · Gesamt: {employee.total_days} Tage</>
              )}
            </SheetDescription>
          </SheetHeader>

          <div className="mt-4">
            <Button className="rounded-full w-full" onClick={openNew} data-testid="sheet-new-vacation-btn">
              <Plus className="mr-2 h-4 w-4" /> Urlaub eintragen
            </Button>
          </div>

          <div className="mt-5 space-y-3" data-testid="sheet-vacation-list">
            {loading ? (
              [1, 2, 3].map((i) => <Skeleton key={i} className="h-20 rounded-lg" />)
            ) : vacations.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">Keine Urlaube vorhanden.</p>
            ) : (
              vacations.map((v) => (
                <div
                  key={v.id}
                  className={`rounded-lg border border-border/60 p-3 ${v.status === 'cancelled' || v.status === 'rejected' ? 'opacity-60' : ''}`}
                  data-testid={`sheet-vacation-${v.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-sm">
                          {format(parseISO(v.start_date), 'dd.MM.yyyy', { locale: de })} – {format(parseISO(v.end_date), 'dd.MM.yyyy', { locale: de })}
                        </span>
                        {getStatusBadge(v.status)}
                        {v.is_retroactive && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">rückwirkend</span>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {v.days_count} Tag{v.days_count === 1 ? '' : 'e'} · {v.vacation_type}
                        {v.notes ? ` · ${v.notes}` : ''}
                      </p>
                    </div>
                  </div>
                  {editable(v) && (
                    <div className="flex gap-1 mt-2 justify-end">
                      <Button variant="ghost" size="sm" className="h-8 rounded-full" onClick={() => openEdit(v)} data-testid={`sheet-edit-${v.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Bearbeiten
                      </Button>
                      {v.days_count > 1 && (
                        <Button variant="ghost" size="sm" className="h-8 rounded-full" onClick={() => setRemoveVacation(v)} data-testid={`sheet-remove-days-${v.id}`}>
                          <CalendarX className="h-3.5 w-3.5 mr-1" /> Tage
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 rounded-full text-destructive hover:text-destructive"
                        onClick={() => handleDelete(v)}
                        disabled={busyId === v.id}
                        data-testid={`sheet-delete-${v.id}`}
                      >
                        {busyId === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                      </Button>
                    </div>
                  )}
                  {Array.isArray(v.history) && v.history.length > 0 && (
                    <div className="mt-2 pt-2 border-t border-border/50">
                      <button
                        type="button"
                        onClick={() => toggleHistory(v.id)}
                        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                        data-testid={`sheet-history-toggle-${v.id}`}
                      >
                        <History className="h-3.5 w-3.5" />
                        Verlauf ({v.history.length})
                        <ChevronDown className={`h-3.5 w-3.5 transition-transform ${expanded.has(v.id) ? 'rotate-180' : ''}`} />
                      </button>
                      {expanded.has(v.id) && (
                        <ol className="mt-2 space-y-2 border-l-2 border-border/60 pl-3" data-testid={`sheet-history-${v.id}`}>
                          {[...v.history].reverse().map((h, i) => (
                            <li key={i} className="text-xs">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium">{ACTION_LABELS[h.action] || h.action}</span>
                                <span className="text-muted-foreground">{fmtDateTime(h.at)} · {h.by}</span>
                              </div>
                              {h.summary && <p className="text-muted-foreground mt-0.5">{h.summary}</p>}
                              {Array.isArray(h.changes) && h.changes.length > 0 && (
                                <ul className="mt-0.5 space-y-0.5">
                                  {h.changes.map((c, ci) => (
                                    <li key={ci} className="text-muted-foreground">
                                      <span className="font-medium">{c.label}:</span>{' '}
                                      <span className="line-through opacity-70">{c.from || '—'}</span>
                                      {' → '}
                                      <span className="text-foreground">{c.to || '—'}</span>
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </li>
                          ))}
                        </ol>
                      )}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>

      <VacationEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        mode={editorMode}
        vacation={editorVacation}
        employeeId={employee?.employee_id}
        employeeName={employee?.employee_name}
        onSaved={afterChange}
      />

      <RemoveDaysDialog
        open={!!removeVacation}
        onOpenChange={(o) => { if (!o) setRemoveVacation(null); }}
        vacation={removeVacation}
        onDone={afterChange}
      />
    </>
  );
}

export default EmployeeVacationsSheet;
